/** Route tree. Screens load lazily so the first paint stays inside the 300 KB budget (spec §12.1).
 * Permission keys per path live in routes.ts; AppShell checks them for every screen inside it. */
import type { ComponentType } from 'react'
import type { RouteObject } from 'react-router'
import { NotFound } from './Forbidden'
import { AnonymousOnly, RequireStep, RequireUser } from './guards'
import { AppShell } from './shell/AppShell'
import { AuthLayout } from './shell/AuthLayout'

const screen = (load: () => Promise<{ default: ComponentType }>) => async () => ({ Component: (await load()).default })

function HydrateFallback() {
  return null
}

export const appRoutes: RouteObject[] = [
  {
    HydrateFallback,
    element: <AuthLayout />,
    children: [
      {
        element: <AnonymousOnly />,
        children: [
          { path: '/login', lazy: screen(() => import('@/modules/accounts/Login')) },
          { path: '/forgot-password', lazy: screen(() => import('@/modules/accounts/ForgotPassword')) },
          { path: '/reset-password/:token', lazy: screen(() => import('@/modules/accounts/ResetPassword')) },
        ],
      },
      {
        element: <RequireStep step="totp" />,
        children: [{ path: '/login/2fa', lazy: screen(() => import('@/modules/accounts/TwoFactor')) }],
      },
      {
        element: <RequireStep step="totp_enrolment" allowReady />,
        children: [{ path: '/enrol-2fa', lazy: screen(() => import('@/modules/accounts/EnrolTwoFactor')) }],
      },
      {
        element: <RequireStep step="password_change" allowReady />,
        children: [{ path: '/change-password', lazy: screen(() => import('@/modules/accounts/ChangePassword')) }],
      },
    ],
  },
  {
    HydrateFallback,
    element: <RequireUser />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/', lazy: screen(() => import('@/modules/core/Home')) },
          { path: '/me', lazy: screen(() => import('@/modules/accounts/MyDetails')) },
          { path: '/organizations', lazy: screen(() => import('@/modules/organizations/Organizations')) },
          { path: '/organizations/:id', lazy: screen(() => import('@/modules/organizations/OrganizationDetail')) },
          { path: '/enquiries', lazy: screen(() => import('@/modules/enquiries/Enquiries')) },
          { path: '/enquiries/new', lazy: screen(() => import('@/modules/enquiries/NewEnquiry')) },
          { path: '/enquiries/:id', lazy: screen(() => import('@/modules/enquiries/EnquiryDetail')) },
          { path: '/tasks', lazy: screen(() => import('@/modules/tasks/Tasks')) },
          { path: '/tasks/new', lazy: screen(() => import('@/modules/tasks/NewTask')) },
          { path: '/tasks/:id', lazy: screen(() => import('@/modules/tasks/TaskDetail')) },
          { path: '/invoices', lazy: screen(() => import('@/modules/sales/Invoices')) },
          { path: '/invoices/:id', lazy: screen(() => import('@/modules/sales/InvoiceDetail')) },
          { path: '/quotes', lazy: screen(() => import('@/modules/quotes/Quotes')) },
          { path: '/quotes/new', lazy: screen(() => import('@/modules/quotes/NewQuote')) },
          { path: '/quotes/:id', lazy: screen(() => import('@/modules/quotes/QuoteDetail')) },
          { path: '/products', lazy: screen(() => import('@/modules/products/Products')) },
          { path: '/products/:id', lazy: screen(() => import('@/modules/products/ProductDetail')) },
          { path: '/stock', lazy: screen(() => import('@/modules/stock/StockList')) },
          { path: '/catalogue/lists', lazy: screen(() => import('@/modules/products/CatalogueLists')) },
          { path: '/imports', lazy: screen(() => import('@/modules/imports/Imports')) },
          { path: '/import-review', lazy: screen(() => import('@/modules/import_review/ImportReview')) },
          { path: '/import-review/:id', lazy: screen(() => import('@/modules/import_review/ReviewDetail')) },
          { path: '/imports/new', lazy: screen(() => import('@/modules/imports/NewImport')) },
          { path: '/imports/:id', lazy: screen(() => import('@/modules/imports/ImportJob')) },
          { path: '/admin/users', lazy: screen(() => import('@/modules/accounts/Users')) },
          { path: '/admin/users/:id', lazy: screen(() => import('@/modules/accounts/UserDetail')) },
          { path: '/admin/permissions', lazy: screen(() => import('@/modules/accounts/Permissions')) },
          { path: '/admin/audit', lazy: screen(() => import('@/modules/audit/AuditScreen')) },
          { path: '/admin/settings', lazy: screen(() => import('@/modules/core/Settings')) },
          { path: '/ace', lazy: screen(() => import('@/modules/ace/AceReports')) },
          { path: '/help/:module', lazy: screen(() => import('@/modules/core/Help')) },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]
